import { API_BASE_URL } from "../lib/constants";
import { getAuthToken } from "../lib/api/client";
import {
  ConversationListResponse,
  ConversationDetailResponse,
  ConversationListParams,
} from "../types/conversation";

export class ConversationAccessError extends Error {
  status: number;
  detail?: string;

  constructor(status: number, message: string, detail?: string) {
    super(message);
    this.name = "ConversationAccessError";
    this.status = status;
    this.detail = detail;
  }
}

class ConversationService {
  private baseUrl = `${API_BASE_URL}/admin/conversations`;

  private async getAuthHeaders(): Promise<HeadersInit> {
    const token = await getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  }

  async getConversations(
    params: ConversationListParams = {}
  ): Promise<ConversationListResponse> {
    const searchParams = new URLSearchParams();

    if (params.page !== undefined) {
      searchParams.append("page", params.page.toString());
    }
    if (params.size !== undefined) {
      searchParams.append("size", params.size.toString());
    }
    if (params.user_id) {
      searchParams.append("user_id", params.user_id);
    }

    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}?${searchParams.toString()}`, {
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch conversations: ${response.statusText}`);
    }

    return response.json();
  }

  async getConversationDetail(
    conversationId: string
  ): Promise<ConversationDetailResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/${conversationId}`, {
      headers,
    });

    if (!response.ok) {
      // Handle authorization errors (401 Unauthorized or 403 Forbidden)
      if (response.status === 401 || response.status === 403) {
        // Parse error detail from response
        try {
          const errorData = await response.json();
          throw new ConversationAccessError(
            response.status,
            "Access denied to this conversation",
            errorData.detail ||
              "You don't have permission to access this conversation"
          );
        } catch (e) {
          if (e instanceof ConversationAccessError) throw e;
          throw new ConversationAccessError(
            response.status,
            "Access denied to this conversation",
            "You don't have permission to access this conversation"
          );
        }
      }
      if (response.status === 404) {
        throw new Error("Conversation not found");
      }
      throw new Error(
        `Failed to fetch conversation details: ${response.statusText}`
      );
    }

    return response.json();
  }

  async deleteConversation(conversationId: string): Promise<void> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/${conversationId}`, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("Conversation not found");
      }
      throw new Error(`Failed to delete conversation: ${response.statusText}`);
    }
  }
}

export const conversationService = new ConversationService();
